import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronsDown,
  ExternalLink,
  X,
} from 'lucide-react';
import type { OnboardedData } from '@/components/smartai/api/getComponentList';
import { getFileContent } from '@/components/smartai/api/getFileContent';
import type {
  CompareResponse,
  CompareFile,
} from '@/components/smartai/api/compareBuildDiff';
import CodeEditor from '@/components/common/CodeEditor';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { MetricCard } from '@/components/ui/metric-card';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  BuildDiffCardHeader,
  buildDiffEntityDetailsHeaderClass,
  buildDiffEntityDetailsPanelClass,
  buildDiffEntityDetailsTitleClass,
  buildDiffEntityGridClass,
  buildDiffEntityHiddenNoteClass,
  buildDiffEntityListClass,
  buildDiffEntityRowClass,
  buildDiffEntityRowNameClass,
  buildDiffEntityRowTypeClass,
  buildDiffEntitySectionClass,
  buildDiffEntitySectionCountClass,
  buildDiffEntitySectionTitleClass,
  buildDiffMetricsGridClass,
} from './build-diff-layout';

interface EntityStats {
  added: number;
  modified: number;
  deleted: number;
  unchanged?: number;
}

interface EntitySummary {
  methods?: EntityStats;
  functions?: EntityStats;
  classes?: EntityStats;
  branches?: EntityStats;
  loops?: EntityStats;
}

interface EntityItem {
  entityName: string;
  entityType: string;
}

interface CodeDetails extends CompareFile {
  content?: string;
  language?: string;
  executable?: number[];
  covered?: number[];
  uncovered?: number[];
  nonExecutable?: number[];
}

const entityConfig = [
  { key: 'methods' as const, label: 'Methods' },
  { key: 'functions' as const, label: 'Functions' },
  { key: 'classes' as const, label: 'Classes' },
  { key: 'branches' as const, label: 'Branches' },
  { key: 'loops' as const, label: 'Loops' },
] as const;

const statusConfig = [
  { key: 'added' as const, label: 'added', prefix: '+' },
  { key: 'modified' as const, label: 'modified', prefix: '' },
  { key: 'deleted' as const, label: 'deleted', prefix: '' },
] as const;

const entityBadgeClass: Record<
  'added' | 'modified' | 'deleted' | 'unchanged',
  string
> = {
  added: 'border border-solid border-[#81c784] bg-[#e8f5e9] text-[#2e7d32]',
  modified: 'border border-solid border-[#f9a825] bg-[#fff9e6] text-[#f57c00]',
  deleted: 'border border-solid border-[#ef9a9a] bg-[#ffeae7] text-[#c62828]',
  unchanged: 'border border-solid border-[#bdbdbd] bg-[#f5f5f5] text-[#757575]',
};

const entityStatusBadgeVariant: Record<
  'added' | 'modified' | 'deleted' | 'unchanged',
  'success' | 'warning' | 'danger' | 'info-outlined'
> = {
  added: 'success',
  modified: 'warning',
  deleted: 'danger',
  unchanged: 'info-outlined',
};

const fileStatusVariant = (
  status?: string,
): 'success' | 'warning' | 'danger' | 'info-outlined' => {
  if (status === 'added') return 'success';
  if (status === 'deleted') return 'danger';
  if (status === 'modified') return 'warning';
  return 'info-outlined';
};

const ChangeSummary: React.FC<{ data: EntitySummary }> = ({ data }) => (
  <div className={buildDiffEntityGridClass}>
    {entityConfig.map(({ key, label }) => {
      const stats = data[key];
      if (!stats) return null;
      return (
        <div
          key={key}
          className="min-w-0 rounded-lg border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] p-3.5 transition-shadow hover:shadow-[0_4px_12px_rgba(0,70,184,0.1)] sm:p-4">
          <div className="mb-2.5 text-sm font-semibold text-[var(--inv-text)]">
            {label}
          </div>
          <div className="grid min-w-0 grid-cols-3 gap-1.5">
            {statusConfig.map(({ key: statKey, label: statLabel, prefix }) => (
              <div
                key={statKey}
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 sm:px-1.5 sm:py-2',
                  entityBadgeClass[statKey],
                )}>
                <span className="text-base font-bold leading-none tabular-nums sm:text-lg min-[1800px]:text-xl">
                  {prefix}
                  {stats[statKey] ?? 0}
                </span>
                <span className="max-w-full truncate text-[0.65rem] font-normal leading-none tracking-wide opacity-80 sm:text-xs">
                  {statLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const EntityBadge = ({
  status,
  count,
}: {
  status: 'added' | 'modified' | 'deleted' | 'unchanged';
  count: number;
}) => {
  if (count === 0) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-xs font-medium',
        entityBadgeClass[status],
      )}>
      {status === 'added' && '+'}
      {status === 'deleted' && '-'}
      {status === 'modified' && '*'}
      {count}
    </span>
  );
};

const EntityDetailRow = ({
  entity,
  status,
}: {
  entity: EntityItem;
  status: 'added' | 'modified' | 'deleted' | 'unchanged';
}) => (
  <div className={buildDiffEntityRowClass}>
    <div className="min-w-0 flex-1">
      <code className={buildDiffEntityRowNameClass}>{entity.entityName}</code>
      <span className={buildDiffEntityRowTypeClass}>
        {entity.entityType.replace(/_/g, ' ')}
      </span>
    </div>
    <StatusBadge
      value={status}
      variant={entityStatusBadgeVariant[status]}
      className="shrink-0 capitalize"
    />
  </div>
);

interface CodeDiffViewerProps {
  diffData: CompareResponse;
  onBoardedData: OnboardedData;
}

const CodeDiffViewer = ({ diffData, onBoardedData }: CodeDiffViewerProps) => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [codeDetails, setCodeDetails] = useState<CodeDetails | null>(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);

  const toggleRow = (index: number) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleViewCode = async (file: CompareFile) => {
    setCodeDetails({ ...file });
    setShowDetailModal(true);
    setIsLoadingCode(true);
    try {
      const codeRes = await getFileContent({
        repoUrl: onBoardedData?.repoUrl,
        branchName: onBoardedData?.branchName || '',
        filePath: file?.filePath || file?.fileName || '',
        scanId: onBoardedData?.scanId,
        pat: onBoardedData?.pat,
      });

      const coveredArray =
        codeRes?.data?.coverage?.linesOfCodeMode?.coveredLines ?? [];
      const executableLines =
        codeRes?.data?.lineClassification?.executable ?? [];
      const uncoveredLines = executableLines.filter(
        (l: number) => !coveredArray.includes(l),
      );

      setCodeDetails({
        ...file,
        content: codeRes?.data?.content,
        language: codeRes?.data?.language,
        executable: executableLines,
        covered: coveredArray,
        uncovered: uncoveredLines,
        nonExecutable: codeRes?.data?.lineClassification?.nonExecutable ?? [],
      });
    } catch {
      // ignore load errors
    } finally {
      setIsLoadingCode(false);
    }
  };

  useEffect(() => {
    if (expandedRow !== null) {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandedRow]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'filePath',
        header: 'File Path',
        render: (row: CompareFile) => (
          <span
            className="block max-w-[28rem] truncate font-mono text-[0.8125rem] text-[var(--inv-accent)]"
            title={row.filePath}>
            {row.filePath}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row: CompareFile) => (
          <StatusBadge
            value={row.status ?? 'modified'}
            variant={fileStatusVariant(row.status)}
          />
        ),
      },
      {
        key: 'lines',
        header: 'Lines',
        render: (row: CompareFile) => (
          <div className="text-sm">
            <span className="text-[#2e7d32]">+{row.linesAdded}</span>
            <span className="mx-1 text-[#9e9e9e]">/</span>
            <span className="text-[#c62828]">-{row.linesDeleted}</span>
          </div>
        ),
      },
      {
        key: 'functions',
        header: 'Functions',
        render: (row: CompareFile) => (
          <div className="flex flex-wrap justify-center gap-1">
            <EntityBadge
              status="added"
              count={row.entities?.functions?.added?.length || 0}
            />
            <EntityBadge
              status="modified"
              count={row.entities?.functions?.modified?.length || 0}
            />
            <EntityBadge
              status="deleted"
              count={row.entities?.functions?.deleted?.length || 0}
            />
          </div>
        ),
        className:
          'w-[8.25rem] min-w-[8.25rem] text-center [&>div]:justify-center',
      },
      {
        key: 'methods',
        header: 'Methods',
        render: (row: CompareFile) => (
          <div className="flex flex-wrap justify-center gap-1">
            <EntityBadge
              status="added"
              count={row.entities?.methods?.added?.length || 0}
            />
            <EntityBadge
              status="modified"
              count={row.entities?.methods?.modified?.length || 0}
            />
            <EntityBadge
              status="deleted"
              count={row.entities?.methods?.deleted?.length || 0}
            />
          </div>
        ),
        className:
          'w-[8.25rem] min-w-[8.25rem] text-center [&>div]:justify-center',
      },
      {
        key: 'classes',
        header: 'Classes',
        render: (row: CompareFile) => (
          <div className="flex flex-wrap justify-center gap-1">
            <EntityBadge
              status="added"
              count={row.entities?.classes?.added?.length || 0}
            />
            <EntityBadge
              status="modified"
              count={row.entities?.classes?.modified?.length || 0}
            />
            <EntityBadge
              status="deleted"
              count={row.entities?.classes?.deleted?.length || 0}
            />
          </div>
        ),
        className:
          'w-[8.25rem] min-w-[8.25rem] text-center [&>div]:justify-center',
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (row: CompareFile) => {
          const index = diffData.files.indexOf(row);
          const isExpanded = expandedRow === index;
          return (
            <div className="flex gap-2">
              {row.status !== 'deleted' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => void handleViewCode(row)}
                  aria-label="View code">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => toggleRow(index)}
                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}>
                <ChevronsDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    isExpanded && 'rotate-180',
                  )}
                />
              </Button>
            </div>
          );
        },
      },
    ],
    [diffData.files, expandedRow],
  );

  const expandedFile =
    expandedRow !== null ? diffData.files[expandedRow] : undefined;

  return (
    <div className="min-w-0">
      <div className={buildDiffMetricsGridClass}>
        <MetricCard title="Total Files" value={diffData.summary.totalFiles} />
        <MetricCard
          title="Lines Added"
          value={diffData.summary.totalLinesAdded}
        />
        <MetricCard
          title="Lines Deleted"
          value={diffData.summary.totalLinesDeleted}
        />
        <MetricCard
          title="Lines Modified"
          value={diffData.summary.totalLinesModified}
        />
      </div>

      {diffData?.entities ? (
        <ChangeSummary data={diffData.entities as EntitySummary} />
      ) : null}

      <div className="min-w-0 w-full">
        <div className="mb-0 border-b border-[#e0e0e0] px-1 py-3 sm:px-2">
          <h2 className="m-0 text-base font-semibold text-[var(--inv-text)] sm:text-lg">
            File-Level Breakdown
          </h2>
        </div>
        <OnboardingTable
          data={diffData.files}
          columns={tableColumns}
          rowKey={row => row.filePath}
          className="[&_table]:min-w-[56rem]"
        />
      </div>

      {expandedFile ? (
        <div ref={detailRef} className={buildDiffEntityDetailsPanelClass}>
          <div className={buildDiffEntityDetailsHeaderClass}>
            <h3 className={buildDiffEntityDetailsTitleClass}>
              Entity details —{' '}
              <span className="break-all font-mono text-[0.8125rem] font-normal text-[var(--inv-accent)]">
                {expandedFile.filePath}
              </span>
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0"
              onClick={() => toggleRow(expandedRow!)}
              aria-label="Close entity details">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {expandedFile.entities &&
            Object.keys(expandedFile.entities).map(entityType => {
              const group = expandedFile.entities![entityType];
              const hasEntities =
                group.added.length > 0 ||
                group.modified.length > 0 ||
                group.deleted.length > 0 ||
                group.unchanged.length > 0;
              if (!hasEntities) return null;
              const total =
                group.added.length +
                group.modified.length +
                group.deleted.length +
                group.unchanged.length;
              return (
                <section key={entityType} className={buildDiffEntitySectionClass}>
                  <h4 className={buildDiffEntitySectionTitleClass}>
                    {entityType}
                    <span className={buildDiffEntitySectionCountClass}>
                      {total} total
                    </span>
                  </h4>
                  <div className="flex flex-col gap-3">
                    {group.added.length > 0 ? (
                      <div>
                        <StatusBadge
                          value={`Added (${group.added.length})`}
                          variant="success"
                          className="mb-2"
                        />
                        <div className={buildDiffEntityListClass}>
                          {group.added.map((entity, idx) => (
                            <EntityDetailRow
                              key={idx}
                              entity={entity}
                              status="added"
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {group.modified.length > 0 ? (
                      <div>
                        <StatusBadge
                          value={`Modified (${group.modified.length})`}
                          variant="warning"
                          className="mb-2"
                        />
                        <div className={buildDiffEntityListClass}>
                          {group.modified.map((entity, idx) => (
                            <EntityDetailRow
                              key={idx}
                              entity={entity}
                              status="modified"
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {group.deleted.length > 0 ? (
                      <div>
                        <StatusBadge
                          value={`Deleted (${group.deleted.length})`}
                          variant="danger"
                          className="mb-2"
                        />
                        <div className={buildDiffEntityListClass}>
                          {group.deleted.map((entity, idx) => (
                            <EntityDetailRow
                              key={idx}
                              entity={entity}
                              status="deleted"
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {group.unchanged.length > 0 &&
                    group.unchanged.length <= 5 ? (
                      <div>
                        <StatusBadge
                          value={`Unchanged (${group.unchanged.length})`}
                          variant="info-outlined"
                          className="mb-2"
                        />
                        <div className={buildDiffEntityListClass}>
                          {group.unchanged.map((entity, idx) => (
                            <EntityDetailRow
                              key={idx}
                              entity={entity}
                              status="unchanged"
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {group.unchanged.length > 5 ? (
                      <p className={buildDiffEntityHiddenNoteClass}>
                        {group.unchanged.length} unchanged {entityType} (hidden
                        for brevity)
                      </p>
                    ) : null}
                  </div>
                </section>
              );
            })}
        </div>
      ) : null}

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-h-[90vh] w-[92vw] max-w-[min(92vw,72rem)] overflow-hidden p-6">
          <DialogHeader>
            <DialogTitle className="break-all pr-8 font-mono text-base">
              {codeDetails?.fileName || codeDetails?.filePath || ''}
            </DialogTitle>
          </DialogHeader>
          {isLoadingCode ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : codeDetails ? (
            <div
              className={cn(
                'grid gap-4 overflow-y-auto',
                codeDetails.status === 'added'
                  ? 'grid-cols-1'
                  : 'grid-cols-1 lg:grid-cols-2',
              )}>
              {codeDetails.status !== 'added' ? (
                <div>
                  <p className="mb-4 text-xl font-semibold">
                    Build A: {diffData.buildA}
                  </p>
                  <CodeEditor
                    instanceId={`buildA-${codeDetails.filePath}`}
                    codeFromApi={codeDetails.old_content ?? ''}
                    language={codeDetails.filePath}
                    deletions={codeDetails.deletedLineNumbers ?? []}
                  />
                </div>
              ) : null}
              <div>
                <p className="mb-4 text-xl font-semibold">
                  Build B: {diffData.buildB}
                </p>
                <CodeEditor
                  instanceId={`buildB-${codeDetails.filePath}`}
                  codeFromApi={
                    codeDetails.new_content ?? codeDetails.content ?? ''
                  }
                  language={codeDetails.filePath}
                  additions={codeDetails.addedLineNumbers ?? []}
                  modified={(codeDetails.modifiedLineNumbers ?? []).map(
                    ([, newLine]) => newLine,
                  )}
                  executable={codeDetails.executable ?? []}
                  nonExecutable={codeDetails.nonExecutable ?? []}
                />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CodeDiffViewer;
